import {useQueryClient} from "@tanstack/react-query";
import {Calendar, CarFront, ChevronRight, CircleStop, FileText, MapPin} from "lucide-react";
import {useEffect, useState} from "react";

import {cn} from "@/lib/utils";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import ConfirmationModal from "@/components/ui/confirm-dialog";
import {BadgeOverflowList, CoordinatesRequiredGate} from "@/src/modules/core/components";
import {useModal, useToast} from "@/src/modules/core/hooks";
import {
  prewarmCoordinatesForGate,
  prewarmGateCoordinatesOnPointerDown,
  useComplianceGateCoordinates,
} from "@/src/modules/core/hooks/use-compliance-gate-coordinates";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {formatDate, formatTime, moment} from "@/src/modules/core/lib";
import {PatrolVehicleConditionDialog} from "@/src/modules/company-vehicles/components";
import {
  shouldPromptPatrolEndConditionCheck,
  shouldPromptPatrolStartConditionCheck,
} from "@/src/modules/company-vehicles/lib/patrol-vehicle-condition";
import {formatPatrolVehicleDisplay} from "@/src/modules/company-vehicles/lib/format-patrol-vehicle-display";
import {
  getCurrentShiftCompanyVehicle,
  listShiftCompanyVehicles,
} from "@/src/modules/company-vehicles/lib/shift-company-vehicles";
import {sortShiftPropertiesPrimaryFirst} from "@/src/modules/shifts/lib/helpers";
import {Shift, ShiftType} from "@/src/modules/shifts/types";
import type {ShiftEndVehicleConditionCheck} from "../../types";

import {useShiftEnd} from "../../hooks";
import {finalizeShiftEndSession} from "../../lib/finalize-shift-end-session";
import {showPatrolCheckpoints} from "../../lib/show-patrol-checkpoints";
import {PropertyInfoModal} from "../property-info-modal";
import {useTranslation} from "react-i18next";

interface ActiveShift {
  shift: Shift;
}

export function ActiveShift({shift}: ActiveShift) {
  const {t} = useTranslation("officer");
  const queryClient = useQueryClient();
  const toast = useToast();
  const {submitBlockedByLocation} = useComplianceGateCoordinates(true);
  const {mutateAsync, isPending} = useShiftEnd();
  const [gpsBusy, setGpsBusy] = useState(false);
  const toDateTime = moment(shift.extended_end_datetime ?? shift.to_datetime);
  const {modalKey, itemId, onOpenModal, onCloseModal} = useModal();
  const [patrolDialog, setPatrolDialog] = useState<{
    mode: "start" | "end";
    intent: "end-shift" | "swap";
    shiftId: number;
  } | null>(null);
  const usePatrolVehicleEndFlow = shouldPromptPatrolEndConditionCheck(shift);
  const now = moment();
  const isPastScheduledEnd = now.isAfter(toDateTime);
  const endSubtitle = isPastScheduledEnd
    ? `Should have ended ${toDateTime.fromNow()}.`
    : toDateTime.isAfter(now)
      ? `Ends ${toDateTime.fromNow()}.`
      : "Scheduled end time is now.";
  const isPatrolShift = shift.type === ShiftType.PATROL;
  // Routed patrols hide Properties badges (checklist lives in its own full-width card on Home).
  const hidePropertiesForCheckpoints = showPatrolCheckpoints(shift);
  const patrolVehicles = isPatrolShift ? listShiftCompanyVehicles(shift) : [];
  const patrolVehiclesNewestFirst = [...patrolVehicles].reverse();
  const propertiesOrdered = sortShiftPropertiesPrimaryFirst(shift.properties);
  const currentCompanyVehicle = isPatrolShift
    ? getCurrentShiftCompanyVehicle(shift)
    : null;
  const canSwapVehicle =
    usePatrolVehicleEndFlow &&
    shift.status === "active" &&
    shift.vehicle_condition_cycle_status === "started" &&
    currentCompanyVehicle != null;

  const handleEndShift = async (vehicleConditionCheck?: ShiftEndVehicleConditionCheck) => {
    setGpsBusy(true);
    try {
      const gps = await resolveShiftGpsForSubmit(shift, "shift_end");
      if (!gps) {
        return;
      }
      const today = moment();
      const data = {
        shift_id: shift.shift_id,
        date: today.format("YYYY-MM-DD"),
        time: today.format("HH:mm:ss"),
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        ...(vehicleConditionCheck ? {vehicle_condition_check: vehicleConditionCheck} : {}),
      };
      const result = await mutateAsync(data);
      toast.success(t("toast.shift_ended"));
      finalizeShiftEndSession(result.tokens_revoked, queryClient);
    } catch (e) {
      // already handled
    } finally {
      setGpsBusy(false);
    }
  };

  useEffect(() => {
    if (modalKey === "end-shift-dialog") {
      prewarmCoordinatesForGate();
    }
  }, [modalKey]);

  return (
    <>
      <Card className="flex h-full min-h-0 flex-1 flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Active Shift</CardTitle>
              <CardDescription>{endSubtitle}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-4">
          {/* On lg: Date | Properties | Patrol (or Date | Patrol when checkpoints). Notes always full width after. */}
          <div
            className={cn(
              "grid min-w-0 grid-cols-1 items-start gap-4 lg:gap-6",
              hidePropertiesForCheckpoints ? "lg:grid-cols-2" : "lg:grid-cols-3",
            )}
          >
            <div className="flex shrink-0 items-start gap-3">
              <Calendar className="mt-0.5 size-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Date</p>
                <div className="flex flex-col items-start gap-0">
                  <p className="text-sm text-foreground">{formatDate(shift.from_datetime)}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(shift.from_datetime)} -{" "}
                    {formatTime(shift.extended_end_datetime ?? shift.to_datetime)}
                  </p>
                </div>
              </div>
            </div>

            {!hidePropertiesForCheckpoints ? (
              <div
                className={cn(
                  "flex min-w-0 items-start gap-3",
                  !isPatrolShift && "lg:col-span-2",
                )}
              >
                <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="mb-2 text-sm font-medium text-muted-foreground">Properties</p>
                  {shift.properties.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {propertiesOrdered.map((p) => (
                        <Button
                          key={`property-${p.shift_property_id}`}
                          size="sm"
                          variant={p.assignment_type === "primary" ? "default" : "outline"}
                          onClick={() => onOpenModal("details", p.property.id)}
                        >
                          {p.property.name} <ChevronRight className="size-4" />
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </div>
              </div>
            ) : null}

            {isPatrolShift ? (
              <div className="flex min-w-0 flex-col gap-2">
                <div className="flex min-w-0 items-start gap-3">
                  <CarFront className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="mb-2 text-sm font-medium text-muted-foreground">Patrol vehicle</p>
                    {patrolVehicles.length > 0 ? (
                      <BadgeOverflowList
                        align="start"
                        className="w-full min-w-0"
                        items={patrolVehiclesNewestFirst}
                        maxVisible={1}
                        getKey={(v, i) => `patrol-vehicle-${v.id}-${i}`}
                        popoverTitle="All company vehicles"
                        getLabel={(v) => formatPatrolVehicleDisplay(v)}
                        overflowPairLayout="flex"
                        clampVisibleLabel={false}
                        visibleBadgeClassName="max-w-full whitespace-normal break-words py-1 leading-snug text-left overflow-visible"
                        popoverItemClassName="whitespace-normal break-words"
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">—</p>
                    )}
                  </div>
                </div>
                {canSwapVehicle ? (
                  <div className="pl-7">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="w-full min-[480px]:w-auto"
                      disabled={isPending || submitBlockedByLocation}
                      onClick={() =>
                        setPatrolDialog({
                          mode: "end",
                          intent: "swap",
                          shiftId: shift.shift_id,
                        })
                      }
                    >
                      Swap vehicle
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex items-start gap-3">
            <FileText className="mt-0.5 size-4 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="mb-2 text-sm font-medium">Shift notes</p>
              <p className="text-sm text-muted-foreground">{shift.notes ?? "-"}</p>
            </div>
          </div>

          <div className="mt-auto flex flex-col gap-4">
            {submitBlockedByLocation ? <CoordinatesRequiredGate /> : null}
            <Button
              size="lg"
              className="flex w-full items-center gap-2"
              variant="destructiveOutline"
              disabled={isPending || gpsBusy || submitBlockedByLocation}
              {...prewarmGateCoordinatesOnPointerDown()}
              onClick={() => {
                if (usePatrolVehicleEndFlow) {
                  setPatrolDialog({
                    mode: "end",
                    intent: "end-shift",
                    shiftId: shift.shift_id,
                  });
                } else {
                  onOpenModal("end-shift-dialog");
                }
              }}
            >
              <CircleStop className="size-4" />
              End shift
            </Button>
          </div>
        </CardContent>
      </Card>

      {modalKey === "details" ? (
        <PropertyInfoModal open propertyId={itemId!} onClose={onCloseModal} />
      ) : null}
      {modalKey === "end-shift-dialog" ? (
        <ConfirmationModal
          open
          loading={isPending || gpsBusy}
          title="End Shift"
          text="You will be ending your shift at the current time. Are you sure you want to continue?"
          confirmButtonClassName="bg-red-600 hover:bg-red-700"
          confirmButtonLabel={gpsBusy ? "Getting location…" : "End Shift"}
          onClose={onCloseModal}
          onConfirm={() => void handleEndShift()}
        />
      ) : null}
      {patrolDialog != null ? (
        <PatrolVehicleConditionDialog
          open
          onOpenChange={(open) => {
            if (!open) {
              setPatrolDialog(null);
            }
          }}
          mode={patrolDialog.mode}
          shiftId={patrolDialog.shiftId}
          lockedVehicle={currentCompanyVehicle}
          onRequestEndShift={
            patrolDialog.intent === "end-shift"
              ? (vehicleConditionCheck) => handleEndShift(vehicleConditionCheck)
              : undefined
          }
          onComplete={async () => {
            await queryClient.refetchQueries({queryKey: ["active-shift"]});
            if (patrolDialog.intent === "swap" && patrolDialog.mode === "end") {
              const refreshed = queryClient.getQueryData<{shift: Shift | null}>(["active-shift"]);
              if (refreshed?.shift && shouldPromptPatrolStartConditionCheck(refreshed.shift)) {
                setPatrolDialog({
                  mode: "start",
                  intent: "swap",
                  shiftId: refreshed.shift.shift_id,
                });
                return;
              }
            }
            setPatrolDialog(null);
          }}
        />
      ) : null}
    </>
  );
}
